import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";
import {
  BarTrendChart,
  LineTrendChart,
  PlanDistributionChart,
} from "../../components/admin/TrendChart";
import { StatCard } from "../../components/admin/StatCard";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { AppLink } from "../../components/layout/AppLink";
import { apiError } from "../../lib/apiError";

type PlatformStats = {
  total_users: number;
  active_users: number;
  inactive_users: number;
  email_verified_users: number;
  email_unverified_users: number;
  mfa_enabled_users: number;
  platform_admin_users: number;
  deletion_pending_users: number;
};

type Trends = {
  registrations: { period: string; count: number }[];
  active_users: { period: string; count: number }[];
  mfa_adoptions: { period: string; count: number }[];
  plan_distribution: { plan_slug: string; billing_status: string; count: number }[];
};

export function AdminOverviewPage() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [trends, setTrends] = useState<Trends | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [statsResult, trendsResult] = await Promise.all([
        api.GET("/api/platform/stats"),
        api.GET("/api/platform/analytics/trends", {
          params: { query: { days: 90, bucket: "week" } },
        }),
      ]);

      if (cancelled) {
        return;
      }

      if (statsResult.error) {
        setError(apiError(statsResult.error, "Nie udało się wczytać statystyk platformy."));
        return;
      }

      if (trendsResult.error) {
        setError(apiError(trendsResult.error, "Nie udało się wczytać trendów."));
        return;
      }

      setStats(statsResult.data as PlatformStats);
      setTrends(trendsResult.data as Trends);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const mfaPct =
    stats && stats.total_users > 0
      ? Math.round((stats.mfa_enabled_users / stats.total_users) * 100)
      : 0;
  const verifiedPct =
    stats && stats.total_users > 0
      ? Math.round((stats.email_verified_users / stats.total_users) * 100)
      : 0;

  return (
    <div className="page__content page__content--admin">
      <AdminPageIntro
        actions={
          <>
            <AppLink href="/admin/users" className="btn btn--ghost">
              Zarządzaj użytkownikami
            </AppLink>
            <AppLink href="/admin/security" className="btn btn--ghost">
              Bezpieczeństwo
            </AppLink>
          </>
        }
      >
        Metadane kont użytkowników — bez danych gospodarstw.
      </AdminPageIntro>

      {error ? (
        <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      ) : null}

      <section className="admin-section" aria-label="Statystyki platformy">
        <div className="admin-kpi">
          <StatCard label="Użytkownicy" value={stats ? stats.total_users : "—"} />
          <StatCard label="Aktywni" value={stats ? stats.active_users : "—"} />
          <StatCard
            label="MFA włączone"
            value={stats ? `${stats.mfa_enabled_users} (${mfaPct}%)` : "—"}
          />
          <StatCard
            label="Oczekujące usunięcia"
            value={stats ? stats.deletion_pending_users : "—"}
            to="/admin/deletions"
          />
          <StatCard
            label="Zweryfikowany e-mail"
            value={stats ? `${stats.email_verified_users} (${verifiedPct}%)` : "—"}
          />
          <StatCard label="Administratorzy" value={stats ? stats.platform_admin_users : "—"} />
        </div>
      </section>

      {trends ? (
        <section className="admin-section" aria-label="Trendy platformy">
          <h2 className="admin-section__title">Trendy (ostatnie 12 tygodni)</h2>
          <div className="admin-charts">
            <LineTrendChart title="Rejestracje" data={trends.registrations} />
            <BarTrendChart title="Aktywni użytkownicy" data={trends.active_users} />
            <PlanDistributionChart title="Rozkład planów" data={trends.plan_distribution} />
          </div>
        </section>
      ) : null}
    </div>
  );
}
